import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaChartLine, FaCog } from 'react-icons/fa';

const MetricsDropdown = ({ run, onMetricsSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('system');

  // System metrics categories
  const systemMetricsCategories = [
    {
      id: 'performance',
      name: 'Performance Metrics',
      icon: '⚡',
      metrics: [
        { key: 'execution_duration', label: 'Execution Duration', unit: 'ms' },
        { key: 'latency_ms', label: 'Latency', unit: 'ms' },
        { key: 'tokens_per_second', label: 'Tokens/Second', unit: '' },
        { key: 'response_time', label: 'Response Time', unit: 'ms' }
      ]
    },
    {
      id: 'quality',
      name: 'Quality Metrics',
      icon: '🎯',
      metrics: [
        { key: 'hallucination_score', label: 'Hallucination Score', unit: '%' },
        { key: 'grounding_score', label: 'Grounding Score', unit: '%' },
        { key: 'model_confidence', label: 'Model Confidence', unit: '%' },
        { key: 'complexity_score', label: 'Complexity Score', unit: '%' }
      ]
    },
    {
      id: 'safety',
      name: 'Safety Metrics',
      icon: '🛡️',
      metrics: [
        { key: 'toxicity_score', label: 'Toxicity Score', unit: '%' },
        { key: 'bias_score', label: 'Bias Score', unit: '%' },
        { key: 'sensitivity_score', label: 'Sensitivity Score', unit: '%' },
        { key: 'hate_speech_detected', label: 'Hate Speech Detected', unit: '' },
        { key: 'violence_detected', label: 'Violence Detected', unit: '' }
      ]
    },
    {
      id: 'cost',
      name: 'Cost Metrics',
      icon: '💰',
      metrics: [
        { key: 'estimated_cost', label: 'Estimated Cost', unit: '$' },
        { key: 'total_tokens', label: 'Total Tokens', unit: '' },
        { key: 'input_tokens', label: 'Input Tokens', unit: '' },
        { key: 'output_tokens', label: 'Output Tokens', unit: '' }
      ]
    }
  ];

  // Custom metrics categories
  const customMetricsCategories = [
    {
      id: 'rag',
      name: 'RAG Metrics',
      icon: '📚',
      metrics: [
        { key: 'answer_faithfulness', label: 'Answer Faithfulness', unit: '%' },
        { key: 'answer_relevance', label: 'Answer Relevance', unit: '%' },
        { key: 'hit_rate', label: 'Hit Rate', unit: '%' },
        { key: 'format_correctness', label: 'Format Correctness', unit: '%' },
        { key: 'queries_per_second', label: 'Queries/Second', unit: '' },
        { key: 'error_rate', label: 'Error Rate', unit: '%' }
      ]
    },
    {
      id: 'embedding',
      name: 'Embedding Metrics',
      icon: '🔍',
      metrics: [
        { key: 'retrieval_duration_ms', label: 'Retrieval Duration', unit: 'ms' },
        { key: 'relevance_score', label: 'Relevance Score', unit: '%' },
        { key: 'embedding_generation_time_ms', label: 'Generation Time', unit: 'ms' },
        { key: 'confidence_score', label: 'Confidence Score', unit: '%' }
      ]
         },
     {
       id: 'truthfulness',
       name: 'Truthfulness Metrics',
       icon: '✅',
       metrics: [
         { key: 'high_faithfulness_score', label: 'High Faithfulness', unit: '%' },
         { key: 'moderate_faithfulness_score', label: 'Moderate Faithfulness', unit: '%' },
         { key: 'average_faithfulness', label: 'Average Faithfulness', unit: '%' },
         { key: 'factual_accuracy', label: 'Factual Accuracy', unit: '%' },
         { key: 'claim_verification', label: 'Claim Verification', unit: '%' },
         { key: 'source_attribution', label: 'Source Attribution', unit: '%' },
         { key: 'contradiction_detection', label: 'Contradiction Detection', unit: '%' },
         { key: 'overall_truthfulness', label: 'Overall Truthfulness', unit: '%' }
       ]
     },
     {
       id: 'compliance',
      name: 'Compliance Metrics',
      icon: '📋',
      metrics: [
        { key: 'pii_detected', label: 'PII Detected', unit: '' },
        { key: 'pii_count', label: 'PII Count', unit: '' },
        { key: 'compliance_status', label: 'Compliance Status', unit: '' },
        { key: 'risk_level', label: 'Risk Level', unit: '' },
        { key: 'alerts_triggered', label: 'Alerts Triggered', unit: '' }
      ]
    }
  ];

  const getMetricValue = (metricKey) => {
    if (!run) return null;

    // Check in different possible locations
    const locations = [
      run.core_metrics,
      run.llm_calls?.[0],
      run.ocr_metrics?.[0],
      run.nlp_analytics,
      run.pii_summary,
      run.compliance,
      run.risk_assessment,
      run.alert_triggers
    ];

    for (const location of locations) {
      if (location && location[metricKey] !== undefined) {
        return location[metricKey];
      }
    }

    // Check nested properties
    if (run.llm_calls?.[0]?.latency_ms && metricKey === 'latency_ms') {
      return run.llm_calls[0].latency_ms;
    }

    if (run.execution_duration && metricKey === 'execution_duration') {
      return run.execution_duration;
    }

    if (run.status && metricKey === 'status') {
      return run.status;
    }

    return null;
  };

  const formatMetricValue = (value, unit) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'number') {
      if (unit === '%') {
        return `${(value * 100).toFixed(1)}%`;
      } else if (unit === '$') {
        return `$${value.toFixed(6)}`;
      } else if (unit === 'ms') {
        return `${value.toFixed(0)}ms`;
      } else {
        return value.toLocaleString();
      }
    }

    return String(value);
  };

  const getMetricColor = (value, metricKey) => {
    if (value === null || value === undefined) {
      return 'text-gray-400';
    }

    if (typeof value === 'boolean') {
      return value ? 'text-green-600' : 'text-red-600';
    }

    if (typeof value === 'number') {
      // For percentage-based metrics
      if (metricKey.includes('score') || metricKey.includes('confidence') || metricKey.includes('faithfulness')) {
        const percentage = value * 100;
        if (percentage >= 80) return 'text-green-600';
        if (percentage >= 60) return 'text-yellow-600';
        return 'text-red-600';
      }

      // For error rates
      if (metricKey.includes('error') || metricKey.includes('toxicity') || metricKey.includes('bias')) {
        const percentage = value * 100;
        if (percentage <= 10) return 'text-green-600';
        if (percentage <= 30) return 'text-yellow-600';
        return 'text-red-600';
      }

      // For cost metrics
      if (metricKey.includes('cost')) {
        return 'text-blue-600';
      }
    }

    return 'text-gray-700';
  };

  const handleMetricSelect = (metric) => {
    if (onMetricsSelect) {
      onMetricsSelect(metric);
    }
    setIsOpen(false);
  };

  const currentCategories = selectedCategory === 'system' ? systemMetricsCategories : customMetricsCategories;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center">
          <FaChartLine className="mr-2 text-blue-600" />
          <span>View Metrics</span>
        </div>
        {isOpen ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
      </button>

      {isOpen && (
        <div className="absolute z-50 w-96 mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-4">
            {/* Category Tabs */}
            <div className="flex mb-4 border-b border-gray-200">
              <button
                onClick={() => setSelectedCategory('system')}
                className={`px-4 py-2 text-sm font-medium ${
                  selectedCategory === 'system'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                System Metrics
              </button>
              <button
                onClick={() => setSelectedCategory('custom')}
                className={`px-4 py-2 text-sm font-medium ${
                  selectedCategory === 'custom'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Custom Metrics
              </button>
            </div>

            {/* Metrics Categories */}
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {currentCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center">
                      <span className="mr-2">{category.icon}</span>
                      <h3 className="text-sm font-medium text-gray-700">{category.name}</h3>
                    </div>
                  </div>
                  
                  <div className="p-3">
                    <div className="grid grid-cols-1 gap-2">
                      {category.metrics
                        .filter(metric => {
                          const value = getMetricValue(metric.key);
                          if (value === null || value === undefined) return false;
                          
                          // Always show error rate metrics even if they are zero
                          if (metric.key.toLowerCase().includes('error') || metric.key.toLowerCase().includes('error_rate')) {
                            return true;
                          }
                          
                          if (typeof value === 'number' && value === 0) return false;
                          if (typeof value === 'string' && value === '0') return false;
                          if (typeof value === 'string') {
                            // Handle percentage strings like "0.0%", "0%", etc.
                            if (value === '0.0' || value === '0.0%' || value === '0%') return false;
                            // Handle other zero-like strings
                            if (value.trim() === '0' || value.trim() === '0.0') return false;
                          }
                          return true;
                        })
                        .map((metric) => {
                        const value = getMetricValue(metric.key);
                        const formattedValue = formatMetricValue(value, metric.unit);
                        const color = getMetricColor(value, metric.key);
                        
                        return (
                          <button
                            key={metric.key}
                            onClick={() => handleMetricSelect({
                              key: metric.key,
                              label: metric.label,
                              value: value,
                              formattedValue: formattedValue,
                              unit: metric.unit,
                              category: category.name
                            })}
                            className="flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded border border-transparent hover:border-gray-200"
                          >
                            <div className="flex-1">
                              <div className="text-xs text-gray-500">{metric.label}</div>
                              <div className={`text-sm font-medium ${color}`}>
                                {formattedValue}
                              </div>
                            </div>
                            <FaCog className="text-gray-400 text-xs" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* No metrics available message */}
            {currentCategories.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No {selectedCategory} metrics available for this run.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDropdown; 